import { HistoricalChartButtonComponent } from "./components/historical-chart-button.component";
import { IndicatorLastPriceComponent } from "./components/indicators-last-price.component";
import { LastPriceComponent } from "./components/last-price.component";
import { MainSymbolCurrentPriceComponent } from "./components/main-symbol-current-price.component";
import { ChangeFromCloseLabelComponent } from "./components/change-from-close-label.component";
import { TiChartSeriesComponent } from "./components/ti-chart-series.component";
import { TILogoComponent } from "./components/ti-logo.component";
import { PnPHelpers } from "./helpers/pnp-helpers";
import { TraceLoggingHelpers } from "./helpers/trace-logging-helpers";
import { BaseChartContainer } from "./models/base/base-chart.container";
import { IChartContainer } from "./models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "./models/chart-style/chart-style-settings.model";
import { ScrollMode } from "./models/enum/scroll-mode.enum";
import { Timeframe } from "./models/enum/timeframe.enum";
import { ChartMode } from "./models/window-wrapper/marketing-webchart-window-wrapper.model";
import { WebChartDataService } from "./services/web-chart-data.service";
import { TiChartMenuContainer } from "./ti-chart-menu.container";

export class WebChartsMarketingPnpContainer extends BaseChartContainer {

    public override chartService: WebChartDataService = null as any;

    public baseChart: IChartContainer;
    private pnpSwitchElement: HTMLElement | null = null;
    
    constructor(element: HTMLElement, baseElement: HTMLElement, baseChart: IChartContainer) {
        super();

        this.baseChart = baseChart;
        this.element = element;

        if (!this.element) {
            return;
        }
        
        this.id = baseChart.id + '-pip';
        this.basePath = baseChart.basePath;
        this.index = baseChart.index;
        
        this.element.addClass(this.id);
        
        this.setPnPSize();

        this.marketingWebChartMode = true;
        this.pnpMode = true;
        this.chartService = new WebChartDataService(this, baseElement);
        // this.drawingToolsManager = new DrawingToolsManager(this);

        const chartMenuElement = this.element.findFirst('.chart-menu-box');
        if (chartMenuElement == null) {
            console.error('ERROR: .chart-menu-box element not found, html needs to be udpated')
            return;
        }

        this.pnpSwitchElement = baseChart.getPnPSwitchElement();
        this.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {        
            if (chartStyle.pnpEnabled) {
                this.element.removeClass('hidden');
                this.pnpSwitchElement?.removeClass('hidden');
            } else if (!chartStyle.pnpEnabled) {
                this.element.addClass('hidden');
                this.pnpSwitchElement?.addClass('hidden');
            }
            
            if (!chartStyle.pnpExpanded) {
                this.element.removeClass('show');
                this.element.classList.add('done');
                this.alreadyCickedCollapse = true;
            } else {
                this.element.addClass('show');
            }
        });

        //Note: the menu needs to be created before the chart series, since HighChart Indicators calls IndicatorLastPrice
        this.tiChartMenu = new TiChartMenuContainer(chartMenuElement, this);

        this.createTIChartSeriesComponent();
        this.createIndicatorsLastPriceComponent();
        this.createMainSymbolCurrentPriceComponent();
        this.createLastPriceComponent();
        this.createTILogoComponent();
        this.createHistoricalChartButtonComponent();
        this.createPnPChangeFromCloseLabelComponent();
        this.subscribeChartComponentWithHighChartPopup();
        this.setPnPLeftPosition();
        
        this.subscribeChartRequestingNewSymbol();

        this.onArrowOnOffClick();
        this.onResize();
        this.onDblClick();
        this.onDrag();
    }

    public subscribeLogin() {
        const chartService = this.chartService as WebChartDataService;
        chartService.connection.subcribeToLoginEvent(() => {
            chartService.onLoggedIn();
        })
    }

    private subscribeChartComponentWithHighChartPopup() {
        this.baseChart.tiChartSeries?.subscribePopupChange(this.id, (open: boolean, event: any) => {
            const popup = document.body.findFirst('.highcharts-popup:not(.custom-highchart-popup)');

            if (open) {
                if (!popup) {
                    return;
                }

                if (this.element.classList.contains('show')) {
                    this.element.findFirst('.pnp-on-off-arrow .arrow')?.click();
                }
            }
        });
    }

    private createTIChartSeriesComponent() {
        this.tiChartSeries = new TiChartSeriesComponent(this.element, this);
        this.components.push(this.tiChartSeries);

        const startCalc = () => {      
            this.setCurrentFirstAndLastPointChangesFromCloseColor(this.chartService.lastPrice);
        };

        this.tiChartSeries.subscribeChartLoad(this.id, () => {
            startCalc();
        });

        this.tiChartSeries.subscribeExtremeChange(this.id, () => {
            if (this.chartService.chartMode == ChartMode.Standard) {
                startCalc();
            }
        });

        this.tiChartSeries.subscribeRedrawChange(this.id, () => {
            if (this.chartService.chartMode == ChartMode.Standard) {
                startCalc();
            }
        });
    }

    private createMainSymbolCurrentPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.main-symbol-current-price').forEach((el: HTMLElement) => {
            this.components.push(new MainSymbolCurrentPriceComponent(el, this));
        });
    }

    private createLastPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.main-symbol-last-price').forEach((el: HTMLElement) => {
            this.components.push(new LastPriceComponent(el, this));
        });
    }

    private createIndicatorsLastPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.indicator-last-prices').forEach((el: HTMLElement) => {
            this.components.push(new IndicatorLastPriceComponent(el, this));
        });
    }

    private createTILogoComponent() {
        this.element.querySelectorAll<HTMLElement>('.logo-wrapper').forEach((el: HTMLElement) => {
            this.components.push(new TILogoComponent(el, this));
        });
    }

    private createHistoricalChartButtonComponent() {
        this.element.querySelectorAll<HTMLElement>('.historical-chart-button').forEach((el: HTMLElement) => {
            this.components.push(new HistoricalChartButtonComponent(el, this));
        });
    }

    private createPnPChangeFromCloseLabelComponent() {
        this.components.push(new ChangeFromCloseLabelComponent(this));
    }
    
    private alreadyCickedCollapse = false;
    private onArrowOnOffClick() {
        const arrows = this.element.querySelectorAll<HTMLElement>('.pnp-on-off-arrow .arrow');
        arrows.forEach((el: HTMLElement) => {
            el.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();

                this.element.style.transition = 'left ease-in-out 1s';
                this.element.classList.remove('done');
                arrows.forEach((a: HTMLElement) => {
                    a.addClass('hidden');
                });

                
                this.element.classList.toggle('show');
                this.alreadyCickedCollapse = true;

                TraceLoggingHelpers.log(`PIPState-${this.element.containClass('show') ? 'On' : 'Off'}`);
                this.setPnPLeftPosition();
                this.requestSavePnPState();

                setTimeout(() => {
                    this.element.style.transition = '';
                    arrows.forEach((a: HTMLElement) => {
                        a.removeClass('hidden');
                    });
                    this.element.classList.add('done');
                }, 1000);
            });
        });
    }

    public setPnPSize() {
        const isBannerActive = this.baseChart.element.classList.contains('ai-signal-banner');

        let mainChartElementWidth = this.baseChart.element.clientWidth;
        let mainChartElementHeight = this.baseChart.element.clientHeight;

        let width = mainChartElementWidth / 3;
        let height = mainChartElementHeight / 3;

        if (width < 140) {
            width = 140;
        }

        if (height < 95) {
            height = 95;
        }

        if (height > width) {
            width = height;
        }

        if (mainChartElementWidth <= 320) {
            this.element.addClass('min-tf');
        } else {
            this.element.removeClass('min-tf');
        }

        const newHeight = height + 'px';
        this.element.style.width = width + 'px';
        this.element.style.height = newHeight;

        if (isBannerActive) {
            this.applyBannerActivePnPPositioning();
        } else if (this.element.style.top != '45px') {
            this.element.style.top = 'calc(100% - ' + newHeight + ')';
        }

        // Auto Collapse PIP on small chart:
        // Make the PIP minimum width 210. If the Main chart width goes below 2x that width (420), then collapse the PIP
        // Make the PIP minimum height 140. If the Main chart height goes below 2x that height (280), then collapse the PIP
        const widowWidth = this.body.clientWidth;
        const windowHeight = this.body.clientHeight;

        if (widowWidth >= 420 && windowHeight >= 280) {
            if (!this.alreadyCickedCollapse) {
                this.element.addClass('show');
            }
            // this.pnpSwitchElement?.display();
        } else {
            if (!this.alreadyCickedCollapse) {
                this.element.removeClass('show');
                this.element.classList.add('done');
            }
            // this.pnpSwitchElement?.hide();
        }
        
        this.setPnPLeftPosition();

        setTimeout(() => {
            this.tiChartSeries?.highStockChart?.reflow();
        }, 320);
    }

    // Anchor PnP to bottom-left of .chart-container when banner is active.
    // To remove banner support: delete this method and the two call sites below.
    private applyBannerActivePnPPositioning() {
        const chartContainerEl = this.baseChart.element.querySelector<HTMLElement>('.chart-container');
        if (!chartContainerEl) return;

        const pnpHeight = parseInt(this.element.style.height, 10) || 0;
        const pnpWidth = parseInt(this.element.style.width, 10) || 0;
        const chartBottom = chartContainerEl.offsetTop + chartContainerEl.clientHeight;

        // Sidebar width from explicit class check — avoids relying on offsetLeft which can be unreliable.
        const isSidebarActive = this.baseChart.element.classList.contains('ai-signal-sidebar');
        const sidebarEl = isSidebarActive
            ? this.baseChart.element.querySelector<HTMLElement>('.marketing-ai-signal__sidebar-banner')
            : null;
        const chartLeft = sidebarEl?.offsetWidth ?? 0;

        this.element.style.top = (chartBottom - pnpHeight) + 'px';
        this.element.style.left = this.element.classList.contains('show')
            ? chartLeft + 'px'
            : -pnpWidth + 'px';
    }

    private setPnPLeftPosition() {
        if (this.baseChart.element.classList.contains('ai-signal-banner')) {
            this.applyBannerActivePnPPositioning();
            return;
        }

        if (this.element.classList.contains('right')) {
            if (!this.element.classList.contains('show')) {
                this.element.style.left = '100%';
            } else {
                this.element.style.left = `calc(100% - ${this.element.style.width})`;
            }
        } else {
            if (!this.element.classList.contains('show')) {
                const width = parseInt(this.element.style.width.replace('px', ''), 10);
                this.element.style.left = '-' + width + 'px';
            } else {
                PnPHelpers.setExtraLeft(this.element);
            }
        }
    }

    private onDblClick() {
        this.element.addEventListener('dblclick', (e: any) => {
            if (e.target && (e.target.classList.contains('chart-menu-box') || !!e.target.closest('.chart-menu-box'))) {
                return;
            }

            const baseChart = this.baseChart as any;
            baseChart.pnpSwitchComponent?.togglePnPMode();
            baseChart.pnpSwitchComponent?.requestTimeFrame();
        });
    }

    private onDrag() {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        let dragStart = false;

        this.element.addEventListener('mousedown', (e: MouseEvent) => {
            if (this.baseChart.element.classList.contains('ai-signal-banner')) return;

            const targetElement = e.target as HTMLElement;
            if (dragStart || targetElement.classList.contains('arrow') || targetElement.closest('.pnp-on-off-arrow')) {
                return;
            }

            if (!this.baseChart.scrollingEnabled && this.baseChart.scrollMode == ScrollMode.ClickAndDrag 
                && !targetElement.classList.contains('menu-box')) {
                return;
            }

            dragStart = true;

            pos3 = e.clientX;
            pos4 = e.clientY;

            this.element.addClass('dragging');
        });

        this.body.addEventListener('mousemove', (e: MouseEvent) => {
            if (!dragStart) {
                return;
            }

            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;

            this.element.style.left = (this.element.offsetLeft - pos1) + 'px';
            this.element.style.top = (this.element.offsetTop - pos2) + 'px';
        });        
        
        this.body.addEventListener('mouseleave', () => {
            if (!dragStart) {
                return;
            }

            dragStart = false;
            this.setPnPPositionAfterDrag();
        });

        this.body.addEventListener('mouseup', () => {
            if (!dragStart) {
                return;
            }

            dragStart = false;
            this.setPnPPositionAfterDrag();
        });
    }

    private setPnPPositionAfterDrag() {
        this.element.removeClass('dragging');

        const bodyWidth = this.baseChart.element.clientWidth;
        const bodyHeight = this.baseChart.element.clientHeight;

        this.element.style.transition = 'all ease-in-out 1s';
        setTimeout(() => {
            this.element.style.transition = '';
        });

        if (bodyWidth / 2 < this.element.offsetLeft) {
            this.element.addClass('right');
            if (bodyHeight / 2 < this.element.offsetTop) {
                this.element.style.left = `calc(100% - ${this.element.style.width})`;
                this.element.style.top = `calc(100% - ${this.element.style.height})`;
            } else {
                this.element.style.left = `calc(100% - ${this.element.style.width})`;
                this.element.style.top = `45px`;
            }
        } else {
            this.element.removeClass('right');
            
            PnPHelpers.setExtraLeft(this.element);
            
            if (bodyHeight / 2 < this.element.offsetTop) {
                this.element.style.top = `calc(100% - ${this.element.style.height})`;
            } else {
                this.element.style.top = `45px`;
            }
        }

        this.requestSavePnPState();
    }

    private requestSavePnPState() {
        const expanded = this.element.containClass('show');
        const className = this.element.containClass('right') ? 'right' : '';
        const top = this.element.style.top == '75px';
        const positionKey = className + '_' + top;
        this.chartService.requestSetPnPState(positionKey, expanded, this.tiChartSeries.currentTimeframe)
    }

    private subscribeChartRequestingNewSymbol() {
        this.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            if (!this.tiChartSeries || this.tiChartSeries.symbol != symbol) {
                this.setComponentStateWhenRequestingNewSymbol(symbol, this.tiChartSeries.currentTimeframe);
            }
        });
    }

    public override setComponentStateWhenRequestingNewSymbol(symbol: string, timeframe: Timeframe) {
        if (this.tiChartSeries) {
            this.tiChartSeries.currentTimeframe = timeframe;
            this.tiChartSeries.symbol = symbol;
            this.tiChartSeries.setPanel();
            this.tiChartSeries.hideWithOpacity();
            this.tiChartSeries.highChartAxisBuilder?.reset();
            this.tiChartSeries.tiChartPoints = [];
            this.tiChartSeries.lastDataPoint = null;
        }

        const pipChangeLabelComponent = this.getComponentsByType(ChangeFromCloseLabelComponent)[0] as ChangeFromCloseLabelComponent;
        pipChangeLabelComponent.hide();
        const lastPriceComponent = this.getComponentsByType(LastPriceComponent)[0] as LastPriceComponent;
        lastPriceComponent?.hide();

        const indicatorLastPriceComponent = this.getComponentsByType(IndicatorLastPriceComponent)[0] as IndicatorLastPriceComponent;
        indicatorLastPriceComponent?.hide();
        const mainSymbolCurrentPriceComponent = this.getComponentsByType(MainSymbolCurrentPriceComponent)[0] as MainSymbolCurrentPriceComponent;
        mainSymbolCurrentPriceComponent?.hide();
    }
    
    private resizeTimmer = null as any;
    private onResize() {
        this.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                if (this.baseChart.isPreview) {
                    return;
                }
                
                this.setPnPSize();
                // this.tiChartSeries.start();
            }, 150);
        });
    }
}